<template>
  <SoForm
    ref="form"
    v-model="allFieldsValid"
    class="form"
    data-testid="readingform-meter"
    @submit="validateAndSubmitReadings()"
  >
    <p data-testid="meter-serial-number" class="mb-5">
      Meter: {{ meter.identifier }}
    </p>
    <SoSelect
      v-model="readingType"
      :items="readingTypes"
      :disabled="shouldSubmitAsFirstRead"
      :rules="[readingTypeValidation]"
      class="form__reading-type mb-5"
    />

    <div v-for="(register, index) in meter.registers" :key="index">
      <MeterReadingFormRegister
        v-model="newReadings[register.id]"
        :register="register"
        :show-register-name="meter.registers.length > 1"
      />
      <SoDivider
        v-if="
          meter.registers.length > 1 && index !== meter.registers.length - 1
        "
        class="mt-4 mb-6"
      />
    </div>
    <section>
      <template v-if="allFieldsValid">
        <SoAlert
          v-if="alreadyHasReadForSetDate"
          class="mb-5"
          type="warning"
          data-testid="readingform-readingexists"
          >A reading for {{ formattedDate }} already exists ({{
            readForSetDate?.cumulative
          }})
        </SoAlert>
        <template v-else-if="areEnteredReadsOutOfRange">
          <div class="mb-4">
            <a
              class="text-primary"
              href="javascript:;"
              data-testid="readingform-overridelink"
              @click="isWarningModalShown = true"
            >
              <SoIcon icon="key" />
              <span class="ml-2">Override warnings</span>
            </a>
          </div>
          <SoAlert class="mb-5" type="warning"
            >The reading you have entered is outside of the expected range. If
            you are sure this is the correct customer reading, click "Override
            warnings".
          </SoAlert>
        </template>
      </template>
      <SoAlert
        v-if="submissionErrors.length"
        class="mb-5"
        title="Error"
        type="error"
      >
        <p
          v-for="(submissionError, index) in submissionErrors"
          :key="index"
          class="mb-3"
        >
          {{ submissionError }}
        </p>
      </SoAlert>
      <SoAlert
        v-if="submitReadingsMutator.isSuccess"
        class="mb-5"
        type="success"
        >Readings submitted successfully
      </SoAlert>
      <SoButton
        class="mr-2"
        title="Submit"
        type="primary"
        data-testid="readingform-submitbtn"
        :loading="submitReadingsMutator.isPending"
        :disabled="
          submitReadingsMutator.isPending ||
          submitReadingsMutator.isSuccess ||
          !!(allFieldsValid && !isFormValid)
        "
        submit
        large
      />
      <SoButton title="Reset" large @click="clearForm" />
    </section>
    <MeterReadingOverrideWarningsModal
      v-model="isWarningModalShown"
      :invalid-readings="outOfRangeReadings"
      :submission-in-progress="submitReadingsMutator.isPending"
      @submit="submitReadingsFromModal()"
    />
  </SoForm>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import dayjs from "dayjs"
  import SoAlert from "@/components/SoAlert.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import { formatDate } from "@/helpers/dateHelper"
  import { errorReporter } from "@/services/ErrorService"
  import { ReadingSequenceType } from "@/models/meterReading/meterReading.types"
  import { ReadingRegisterTuple } from "@/mutators/meterReadings/MeterReadingsMutator.types"
  import { SubmitReadingsError } from "@/mutators/meterReadings/processReadingErrors"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import MeterReadingFormRegister from "./MeterReadingFormRegister.vue"
  import MeterReadingOverrideWarningsModal from "./MeterReadingOverrideWarningsModal.vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { IMeter } from "@/models/meter/meter.types"

  export default defineComponent({
    components: {
      SoAlert,
      SoDivider,
      SoButton,
      SoForm,
      SoIcon,
      SoSelect,
      MeterReadingFormRegister,
      MeterReadingOverrideWarningsModal,
    },
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        required: true,
      },
      meter: {
        type: Object as PropType<IMeter>,
        required: true,
      },
      readingDate: {
        type: Object as PropType<Date>,
        required: true,
      },
      shouldSubmitAsFirstRead: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        submitReadingsMutator: useCurrentAccountStore().submitReadings(),
        readingTypes: [ReadingSequenceType.Normal, ReadingSequenceType.First],
        readingType: ReadingSequenceType.Normal,
        newReadings: {} as Record<string, string>,
        submissionErrors: [] as string[],
        allFieldsValid: null,
        isWarningModalShown: false,
      }
    },
    computed: {
      isFormValid() {
        return (
          this.allFieldsValid &&
          !this.alreadyHasReadForSetDate &&
          !this.areEnteredReadsOutOfRange
        )
      },
      alreadyHasReadForSetDate() {
        return !!this.readForSetDate
      },
      readForSetDate() {
        const selectedDate = dayjs(this.readingDate)
        return this.meterpoint.readings.find(
          (reading) =>
            reading.date.isSame(selectedDate, "day") &&
            !this.submitReadingsMutator.isSuccess,
        )
      },
      formattedSsd() {
        return formatDate(dayjs(this.meterpoint.supplyStartDate))
      },
      formattedDate() {
        return formatDate(dayjs(this.readingDate))
      },
      areEnteredReadsOutOfRange() {
        return !!(this.allRegistersPopulated && this.outOfRangeReadings.length)
      },
      allRegistersPopulated() {
        return this.meter.registers.every(
          (register) => !!this.newReadings[register.id],
        )
      },
      outOfRangeReadings() {
        return this.meter.registers.reduce((invalidReadings, register) => {
          const reading = Number(this.newReadings[register.id])

          if (!register.isReadingWithinBounds(reading)) {
            invalidReadings.push([reading, register])
          }
          return invalidReadings
        }, [] as ReadingRegisterTuple[])
      },
    },
    watch: {
      shouldSubmitAsFirstRead: {
        immediate: true,
        handler(shouldSubmitAsFirstRead) {
          if (shouldSubmitAsFirstRead) {
            this.readingType = ReadingSequenceType.First
          }
        },
      },
    },
    methods: {
      removeSuccessStateAfterFormChange() {
        const unsubscribe = this.$watch(
          () => [this.readingDate, this.newReadings, this.readingType],
          () => {
            this.submitReadingsMutator.reset()
            unsubscribe()
          },
          {
            deep: true,
          },
        )
      },
      async submitReadingsFromModal() {
        this.isWarningModalShown = false
        await this.submitReadings()
      },
      clearForm() {
        this.meter.registers.forEach((register) => {
          delete this.newReadings[register.id]
        })
        const form = this.$refs.form as typeof SoForm
        form.resetValidation()
      },
      resetFormMessages() {
        this.submitReadingsMutator.reset()
        this.submissionErrors = []
      },
      async validateAndSubmitReadings() {
        this.resetFormMessages()
        if (this.isFormValid) {
          await this.submitReadings()
        }
      },
      async submitReadings() {
        this.resetFormMessages()
        try {
          const readingRegisterTuples = Object.entries(
            this.newReadings,
          ).map<ReadingRegisterTuple>(([registerId, reading]) => {
            const meterRegister = this.meter.registers.find(
              ({ id }) => Number(registerId) === id,
            )
            if (meterRegister) {
              return [Number(reading), meterRegister]
            }
            throw new Error(`Meter register with id ${registerId} not found.`)
          })

          await this.submitReadingsMutator.mutateAsync({
            meter: this.meter,
            account: this.current.account.data,
            meterpoint: this.meterpoint,
            date: this.readingDate,
            sequenceType: this.readingType,
            readings: readingRegisterTuples,
          })
          this.clearForm()
          this.removeSuccessStateAfterFormChange()
        } catch (error) {
          if (error instanceof SubmitReadingsError) {
            this.submissionErrors = error.messages
          } else {
            this.submissionErrors = ["Unexpected error occurred."]
            errorReporter.report(error)
            console.error(error)
          }
        }
      },
      readingTypeValidation(readingType: string | null) {
        if (readingType !== ReadingSequenceType.First) return true

        const hasFirstReading = this.meterpoint.readings.some(
          (reading) => reading.isOfTypeFirst,
        )
        if (hasFirstReading) {
          return `Reading of type First already exists`
        }

        if (
          !!this.readingDate &&
          !this.meterpoint.inFirstReadPeriod(dayjs(this.readingDate))
        ) {
          return `Chosen date is too far from SSD (${this.formattedSsd})`
        }

        return true
      },
    },
  })
</script>

<style scoped>
  .form {
    line-height: 1.6;
  }

  .form__error {
    line-height: initial;
  }

  .form__reading-type {
    width: 200px;
  }
</style>
